import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useOrders } from '@/hooks/useOrders';
import { useProducts } from '@/hooks/useProducts';
import { useAnalytics } from '@/hooks/useAnalytics';
import { 
  Package, 
  TrendingUp, 
  Users, 
  DollarSign, 
  LogOut,
  Search,
  Plus,
  Edit2,
  Trash2,
  Download,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { ProductsService } from '@/services/firebase.service';
import ProductForm from '@/components/ProductForm';
import jsPDF from 'jspdf';

const BC_LOGO_URL = 'https://customer-assets.emergentagent.com/job_luxury-estore-3/artifacts/czobo02e_B%26C%20LOGO%20-%2001.png';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const { orders, loading: ordersLoading, updateOrderStatus, updateOrderWithTracking, deleteOrder } = useOrders();
  const { products, loading: productsLoading, refetch: refetchProducts } = useProducts();
  const { stats, loading: statsLoading } = useAnalytics();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [trackingModal, setTrackingModal] = useState({ open: false, orderId: null });
  const [trackingId, setTrackingId] = useState('');

  const loading = ordersLoading || statsLoading;

  const handleSignOut = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout');
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    // If changing to Shipped, require tracking ID
    if (newStatus === 'Shipped') {
      setTrackingModal({ open: true, orderId });
      return;
    }
    
    const result = await updateOrderStatus(orderId, newStatus);
    if (result.success) {
      toast.success('Order status updated');
    } else {
      toast.error('Failed to update order status');
    }
  };

  const handleShipWithTracking = async () => {
    if (!trackingId.trim()) {
      toast.error('Please enter a tracking ID');
      return;
    }
    
    const result = await updateOrderWithTracking(trackingModal.orderId, 'Shipped', trackingId.trim());
    if (result.success) {
      toast.success('Order shipped with tracking ID');
      setTrackingModal({ open: false, orderId: null });
      setTrackingId('');
    } else {
      toast.error('Failed to update order');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      return;
    }
    try {
      const result = await deleteOrder(orderId);
      if (result.success) {
        toast.success('Order deleted successfully');
      } else {
        toast.error('Failed to delete order');
      }
    } catch (error) {
      console.error('Error deleting order:', error);
      toast.error('Failed to delete order');
    }
  };

  // Generate PDF Invoice/Bill - Professional Format
  const generateOrderPDF = async (order) => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      
      // === HEADER SECTION ===
      doc.setFillColor(0, 0, 0);
      doc.rect(0, 0, pageWidth, 35, 'F');
      
      // Company Name - Left
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('B&C Store', 20, 20);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('Premium Mobile Accessories', 20, 28);
      
      // Company Contact - Right
      doc.setFontSize(9);
      doc.text('contactus@bcstore.in', pageWidth - 20, 18, { align: 'right' });
      doc.text('www.bcstore.in', pageWidth - 20, 26, { align: 'right' });
      
      // === BILL TITLE ===
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('BILL', pageWidth / 2, 48, { align: 'center' });
      
      // === ORDER INFO LINE ===
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      const orderDate = order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-IN', { 
        year: 'numeric', month: 'short', day: 'numeric'
      }) : 'N/A';
      doc.text(`Order ID: ${order.orderId || 'N/A'}  |  Date: ${orderDate}  |  Payment: ${order.paymentStatus || 'Pending'}`, pageWidth / 2, 56, { align: 'center' });
      
      // === SHIPPING INFORMATION SECTION ===
      let currentY = 68;
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('SHIPPING DETAILS', margin, currentY);
      
      currentY += 3;
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);
      doc.line(margin, currentY, margin + 45, currentY);
      
      currentY += 8;
      
      // Shipping details in two columns
      doc.setFontSize(9);
      const col1X = margin + 5;
      const col2X = pageWidth / 2 + 5;
      const labelWidth = 22;
      
      // Row 1: Name & City
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 100, 100);
      doc.text('Name:', col1X, currentY);
      doc.text('City:', col2X, currentY);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(order.customer?.name || 'N/A', col1X + labelWidth, currentY);
      doc.text(order.customer?.city || 'N/A', col2X + labelWidth, currentY);
      
      currentY += 7;
      // Row 2: Phone & State
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 100, 100);
      doc.text('Phone:', col1X, currentY);
      doc.text('State:', col2X, currentY);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(order.customer?.phone || 'N/A', col1X + labelWidth, currentY);
      doc.text(order.customer?.state || 'N/A', col2X + labelWidth, currentY);
      
      currentY += 7;
      // Row 3: Email & Pincode
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 100, 100);
      doc.text('Email:', col1X, currentY);
      doc.text('Pincode:', col2X, currentY);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(order.customer?.email || 'N/A', col1X + labelWidth, currentY);
      doc.text(order.customer?.pincode || 'N/A', col2X + labelWidth, currentY);
      
      currentY += 7;
      // Row 4: Address
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 100, 100);
      doc.text('Address:', col1X, currentY);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(order.customer?.address || 'N/A', col1X + labelWidth, currentY);
      
      // === ORDER SUMMARY SECTION ===
      currentY += 15;
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('ORDER SUMMARY', margin, currentY);
      
      currentY += 3;
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);
      doc.line(margin, currentY, margin + 45, currentY);
      
      currentY += 8;
      
      // Table Header
      const tableStartY = currentY;
      doc.setFillColor(240, 240, 240);
      doc.rect(margin, currentY - 5, pageWidth - (margin * 2), 10, 'F');
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      
      // Column positions
      const colSNo = margin + 3;
      const colProduct = margin + 18;
      const colCategory = margin + 85;
      const colQty = margin + 120;
      const colPrice = margin + 138;
      const colTotal = pageWidth - margin - 5;
      
      doc.text('S.No', colSNo, currentY);
      doc.text('Product', colProduct, currentY);
      doc.text('Category', colCategory, currentY);
      doc.text('Qty', colQty, currentY);
      doc.text('Price', colPrice, currentY);
      doc.text('Total', colTotal, currentY, { align: 'right' });
      
      currentY += 8;
      
      // Table Items
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      let subtotal = 0;
      
      (order.items || []).forEach((item, index) => {
        const itemTotal = (item.price || 0) * (item.quantity || 1);
        subtotal += itemTotal;
        
        // Check if we need a new page
        if (currentY > pageHeight - 60) {
          doc.addPage();
          currentY = 20;
        }
        
        // Row background for alternating rows
        if (index % 2 === 1) {
          doc.setFillColor(250, 250, 250);
          doc.rect(margin, currentY - 4, pageWidth - (margin * 2), 12, 'F');
        }
        
        // S.No
        doc.setTextColor(0, 0, 0);
        doc.text(`${index + 1}`, colSNo, currentY);
        
        // Product Name
        const productName = (item.title || item.productTitle || 'Product').substring(0, 30);
        doc.setFont('helvetica', 'bold');
        doc.text(productName, colProduct, currentY);
        
        // Color below product name
        if (item.color && item.color !== 'Default') {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7);
          doc.setTextColor(100, 100, 100);
          doc.text(`Color: ${item.color}`, colProduct, currentY + 4);
          doc.setFontSize(8);
        }
        
        // Category
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        const category = (item.category || 'N/A').substring(0, 15);
        doc.text(category, colCategory, currentY);
        
        // Quantity
        doc.text(`${item.quantity || 1}`, colQty, currentY);
        
        // Price
        doc.text(`Rs.${item.price || 0}`, colPrice, currentY);
        
        // Total
        doc.setFont('helvetica', 'bold');
        doc.text(`Rs.${itemTotal}`, colTotal, currentY, { align: 'right' });
        
        currentY += 14;
      });
      
      // Draw table border
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.rect(margin, tableStartY - 5, pageWidth - (margin * 2), currentY - tableStartY + 2);
      
      // === TOTALS SECTION ===
      currentY += 5;
      
      const totalsX = pageWidth - 80;
      
      // Subtotal
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text('Subtotal:', totalsX, currentY);
      doc.setTextColor(0, 0, 0);
      doc.text(`Rs.${subtotal}`, colTotal, currentY, { align: 'right' });
      
      currentY += 7;
      // Shipping
      doc.setTextColor(80, 80, 80);
      doc.text('Shipping:', totalsX, currentY);
      doc.setTextColor(0, 128, 0);
      doc.text('FREE', colTotal, currentY, { align: 'right' });
      
      currentY += 3;
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);
      doc.line(totalsX - 5, currentY, pageWidth - margin, currentY);
      
      currentY += 8;
      // Grand Total
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('GRAND TOTAL:', totalsX, currentY);
      doc.text(`Rs.${order.total || subtotal}`, colTotal, currentY, { align: 'right' });
      
      // === TRACKING INFORMATION ===
      if (order.trackingId) {
        currentY += 15;
        doc.setFillColor(240, 253, 244);
        doc.setDrawColor(34, 197, 94);
        doc.roundedRect(margin, currentY - 3, pageWidth - (margin * 2), 12, 2, 2, 'FD');
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(22, 163, 74);
        doc.text('Tracking ID: ' + order.trackingId, margin + 5, currentY + 5);
      }
      
      // === FOOTER ===
      const footerY = pageHeight - 25;
      
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('Thank you for shopping with B&C Store!', pageWidth / 2, footerY + 2, { align: 'center' });
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('For support: contactus@bcstore.in | www.bcstore.in', pageWidth / 2, footerY + 9, { align: 'center' });
      
      doc.setFontSize(7);
      doc.text('Developed by Venatriks and Doutly', pageWidth / 2, footerY + 15, { align: 'center' });
      
      // Save PDF
      doc.save(`BC_Store_Bill_${order.orderId}.pdf`);
      toast.success('Bill downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate bill');
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.orderId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.customer?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.customer?.phone?.includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || order.orderStatus === statusFilter;
    
    // Date filtering
    let matchesDate = true;
    if (dateFilter !== 'all' && order.createdAt) {
      const orderDate = new Date(order.createdAt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (dateFilter === 'today') {
        matchesDate = orderDate >= today;
      } else if (dateFilter === 'week') {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        matchesDate = orderDate >= weekAgo;
      } else if (dateFilter === 'month') {
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        matchesDate = orderDate >= monthAgo;
      }
    }
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.title?.toLowerCase().includes(productSearch.toLowerCase()) ||
                         product.modelName?.toLowerCase().includes(productSearch.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleSaveProduct = async (productData) => {
    try {
      if (editingProduct) {
        await ProductsService.update(editingProduct.id, productData);
      } else {
        await ProductsService.create(productData);
      }
      setShowProductForm(false);
      setEditingProduct(null);
      refetchProducts();
    } catch (error) {
      throw error;
    }
  };

  const exportToCSV = (data, filename) => {
    if (data.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          // Escape commas and quotes in values
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`${filename} exported successfully!`);
  };

  const handleExportOrders = () => {
    const exportData = filteredOrders.map(order => ({
      'Order ID': order.orderId,
      'Customer Name': order.customer?.name || 'N/A',
      'Email': order.customer?.email || 'N/A',
      'Phone': order.customer?.phone || 'N/A',
      'Total Amount': order.total,
      'Status': order.orderStatus || 'Pending',
      'Date': order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'N/A',
      'Address': `${order.customer?.address}, ${order.customer?.city}, ${order.customer?.pincode}`,
      'Items Count': order.items?.length || 0,
    }));
    exportToCSV(exportData, 'orders');
  };

  const handleExportSales = () => {
    const salesData = filteredOrders
      .filter(order => order.orderStatus !== 'Cancelled')
      .map(order => ({
        'Order ID': order.orderId,
        'Date': order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'N/A',
        'Amount': order.total,
        'Status': order.orderStatus,
        'Payment Status': 'Completed', // You can add payment status field later
      }));
    
    const totalSales = salesData.reduce((sum, order) => sum + parseFloat(order.Amount || 0), 0);
    
    salesData.push({
      'Order ID': '',
      'Date': '',
      'Amount': '',
      'Status': '',
      'Payment Status': ''
    });
    salesData.push({
      'Order ID': 'TOTAL SALES',
      'Date': '',
      'Amount': totalSales.toFixed(2),
      'Status': '',
      'Payment Status': ''
    });
    
    exportToCSV(salesData, 'sales');
  };


  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }
    try {
      await ProductsService.delete(productId);
      toast.success('Product deleted');
      refetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };


  const statsCards = stats ? [
    {
      title: 'Total Revenue',
      value: `₹${stats.totalRevenue.toLocaleString('en-IN')}`,
      icon: DollarSign,
      color: 'bg-green-500',
    },
    {
      title: 'Total Orders',
      value: stats.totalOrders.toString(),
      icon: Package,
      color: 'bg-blue-500',
    },
    {
      title: 'Total Customers',
      value: stats.totalCustomers.toString(),
      icon: Users,
      color: 'bg-purple-500',
    },
    {
      title: 'Products',
      value: stats.totalProducts.toString(),
      icon: TrendingUp,
      color: 'bg-orange-500',
    },
  ] : [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Processing':
        return 'bg-blue-100 text-blue-800';
      case 'Shipped':
        return 'bg-orange-100 text-orange-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'Cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-200 border-t-black rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Tracking ID Modal */}
      {trackingModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Enter Tracking ID</h3>
            <p className="text-gray-600 text-sm mb-4">
              Please enter the tracking ID to mark this order as shipped.
            </p>
            <Input
              type="text"
              placeholder="e.g., TRACK123456789"
              value={trackingId}
              onChange={(e) => setTrackingId(e.target.value)}
              className="mb-4"
            />
            <div className="flex gap-3">
              <Button
                onClick={() => { setTrackingModal({ open: false, orderId: null }); setTrackingId(''); }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleShipWithTracking}
                className="flex-1 bg-black text-white hover:bg-gray-800"
              >
                Ship Order
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">BCStore Admin</h1>
              <p className="text-sm text-gray-600">Welcome back, {currentUser?.email}</p>
            </div>
            <Button
              onClick={handleSignOut}
              variant="outline"
              className="flex items-center space-x-2"
              data-testid="logout-button"
            >
              <LogOut size={18} />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Navigation Tabs */}
        <div className="flex space-x-2 mb-8 overflow-x-auto pb-2">
          {['overview', 'orders', 'products'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab
                  ? 'bg-black text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {statsCards.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`p-3 rounded-full ${stat.color}`}>
                        <stat.icon className="text-white" size={24} />
                      </div>
                    </div>
                    <h3 className="text-gray-600 text-sm mb-1">{stat.title}</h3>
                    <p className="text-2xl font-bold">{stat.value}</p>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Recent Orders */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Recent Orders</h2>
                <Button variant="outline" size="sm" onClick={() => setActiveTab('orders')}>
                  View All ({orders.length})
                </Button>
              </div>
              {orders.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Package size={48} className="mx-auto mb-4 text-gray-300" />
                  <p>No orders yet</p>
                  <p className="text-sm mt-2">Orders will appear here when customers make purchases</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Order ID</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 hidden sm:table-cell">Customer</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600 hidden md:table-cell">Items</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Amount</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.slice(0, 5).map((order) => (
                        <tr key={order.orderId} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-4 px-4 text-sm font-medium">{order.orderId}</td>
                          <td className="py-4 px-4 text-sm hidden sm:table-cell">{order.customer?.name || 'N/A'}</td>
                          <td className="py-4 px-4 text-sm text-gray-600 hidden md:table-cell">{order.items?.length || 0} item(s)</td>
                          <td className="py-4 px-4 text-sm font-medium">₹{order.total}</td>
                          <td className="py-4 px-4">
                            <select
                              value={order.orderStatus}
                              onChange={(e) => handleStatusUpdate(order.orderId, e.target.value)}
                              className={`px-3 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${getStatusColor(order.orderStatus)}`}
                            >
                              <option value="Pending">Pending</option>
                              <option value="Processing">Processing</option>
                              <option value="Shipped">Shipped</option>
                              <option value="Completed">Completed</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </motion.div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                <h2 className="text-2xl font-bold">All Orders ({filteredOrders.length})</h2>
                <div className="flex flex-wrap gap-2">
                  <div className="relative flex-1 sm:flex-none">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <Input 
                      placeholder="Search orders..." 
                      className="pl-10 w-full sm:w-64"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <select
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">Last 7 Days</option>
                    <option value="month">Last 30 Days</option>
                  </select>
                  <select
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="Pending">Pending</option>
                    <option value="Processing">Processing</option>
                    <option value="Shipped">Shipped</option>
                    <option value="Completed">Completed</option>
                  </select>
                  <Button
                    onClick={handleExportOrders}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export Orders
                  </Button>
                  <Button
                    onClick={handleExportSales}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export Sales
                  </Button>
                </div>
              </div>
              
              {filteredOrders.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Package size={48} className="mx-auto mb-4 text-gray-300" />
                  <p>No orders found</p>
                  <p className="text-sm mt-2">
                    {orders.length === 0 ? 'Orders will appear here when customers make purchases' : 'Try adjusting your search or filters'}
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredOrders.map((order) => (
                    <Card key={order.orderId} className="p-4 hover:shadow-md transition-shadow">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h3 className="font-bold">{order.orderId}</h3>
                            <select
                              value={order.orderStatus}
                              onChange={(e) => handleStatusUpdate(order.orderId, e.target.value)}
                              className={`px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${getStatusColor(order.orderStatus)}`}
                            >
                              <option value="Pending">Pending</option>
                              <option value="Processing">Processing</option>
                              <option value="Shipped">Shipped</option>
                              <option value="Completed">Completed</option>
                              <option value="Cancelled">Cancelled</option>
                            </select>
                            {order.trackingId && (
                              <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                                📦 Tracking: {order.trackingId}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            <strong>Customer:</strong> {order.customer?.name || 'N/A'} | {order.customer?.phone || 'N/A'}
                          </p>
                          <p className="text-sm text-gray-600">
                            <strong>Items:</strong> {(order.items || []).map(item => {
                              const category = item.category ? `[${item.category}]` : '';
                              const model = item.model ? `(${item.model})` : '';
                              return `${item.productTitle || item.title} ${category} ${model}`.trim();
                            }).join(', ')}
                          </p>
                          <p className="text-sm text-gray-600">
                            <strong>Address:</strong> {order.customer?.city}, {order.customer?.state} - {order.customer?.pincode}
                          </p>
                        </div>
                        <div className="text-right flex flex-col items-end gap-2">
                          <p className="text-2xl font-bold">₹{order.total}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(order.createdAt).toLocaleDateString('en-IN')}
                          </p>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => generateOrderPDF(order)}
                              className="flex items-center gap-1"
                            >
                              <FileText className="w-4 h-4" />
                              PDF
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteOrder(order.orderId)}
                              className="flex items-center gap-1"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </motion.div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                <h2 className="text-2xl font-bold">Products ({filteredProducts.length})</h2>
                <Button 
                  onClick={() => {
                    setEditingProduct(null);
                    setShowProductForm(true);
                  }}
                  className="bg-brand-blue hover:bg-brand-navy"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Product
                </Button>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    placeholder="Search products..."
                    className="pl-10"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                  />
                </div>
                <select
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  <option value="Screen guard">Screen guard</option>
                  <option value="Camera ring">Camera ring</option>
                  <option value="Charger cable">Charger cable</option>
                  <option value="Airpods">Airpods</option>
                </select>
              </div>
              
              {productsLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue mx-auto"></div>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Package size={48} className="mx-auto mb-4 text-gray-300" />
                  <p>No products yet</p>
                  <p className="text-sm mt-2">Add your first product to get started</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products.map((product) => (
                    <Card key={product.id} className="p-4 hover:shadow-md transition-shadow">
                      <div className="flex gap-2 mb-3">
                        {product.images?.slice(0, 3).map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt={`${product.title} ${idx + 1}`}
                            className="w-20 h-20 object-cover rounded-lg border"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        ))}
                      </div>
                      <h3 className="font-semibold text-lg mb-1 line-clamp-2">{product.title}</h3>
                      {product.modelName && (
                        <p className="text-xs text-gray-600 mb-1">📱 {product.modelName}</p>
                      )}
                      {product.category && (
                        <span className="inline-block px-2 py-1 bg-brand-blue/10 text-brand-blue text-xs rounded-full mb-2">
                          {product.category}
                        </span>
                      )}
                      <div className="mb-2">
                        {product.discountPrice ? (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 line-through text-sm">₹{product.originalPrice}</span>
                            <span className="text-brand-blue font-bold text-xl">₹{product.discountPrice}</span>
                            <span className="text-xs text-green-600 font-medium">
                              {Math.round(((product.originalPrice - product.discountPrice) / product.originalPrice) * 100)}% OFF
                            </span>
                          </div>
                        ) : (
                          <p className="text-brand-blue font-bold text-xl">₹{product.originalPrice || product.price}</p>
                        )}
                      </div>
                      {product.colors && product.colors.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-500 mb-1">Colors:</p>
                          <div className="flex flex-wrap gap-1">
                            {product.colors.map((color, idx) => (
                              <span key={idx} className="px-2 py-1 bg-gray-100 text-xs rounded">
                                {color}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingProduct(product);
                            setShowProductForm(true);
                          }}
                          className="flex-1"
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteProduct(product.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </motion.div>
        )}
      </div>

      {showProductForm && (
        <ProductForm
          product={editingProduct}
          onSave={handleSaveProduct}
          onCancel={() => {
            setShowProductForm(false);
            setEditingProduct(null);
          }}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
